import React from 'react';
import { useABTestStatus } from '../hooks/useABTest';
import abTestingManager from '../utils/abTesting';

/**
 * Componente de debug para A/B Testing
 * Se muestra solo si hay el parámetro ?debug=1 en la URL
 */
const ABTestDebugger = () => {
    const { status, isDevelopment } = useABTestStatus();

    // Verificar parámetro debug en URL
    const urlParams = new URLSearchParams(window.location.search);
    const showDebug = urlParams.get('debug') === '1';

    // Solo mostrar si debug=1 está en la URL
    if (!showDebug) {
        return null;
    }

    // Función para resetear todos los tests
    const handleResetAll = () => {
        abTestingManager.resetAllTests();
        window.location.reload(); // Recargar para mostrar nuevas variantes
    };

    // Función para limpiar caché y reasignar
    const handleClearAndReassign = () => {
        abTestingManager.clearCacheAndReassign();
        window.location.reload(); // Recargar para mostrar nuevas variantes
    };

    // Función para debuggear reasignación de un test específico
    const handleDebugReassign = (testName) => {
        if (window.abTestingDebug) {
            window.abTestingDebug.debugReassign(testName);
            window.location.reload();
        }
    };

    // Función para mostrar weights
    const handleShowWeights = () => {
        if (window.abTestingDebug) {
            window.abTestingDebug.showWeights();
        }
    };

    return (
        <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '15px',
            borderRadius: '8px',
            fontSize: '12px',
            zIndex: 10000,
            maxWidth: '350px',
            border: '2px solid #0060a9',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
            <div style={{ marginBottom: '10px', fontWeight: 'bold', textAlign: 'center' }}>
                🧪 A/B Test Debug
            </div>

            {Object.entries(status).map(([testName, testData]) => (
                <div key={testName} style={{ marginBottom: '8px', padding: '8px', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' }}>
                    <div style={{ fontWeight: 'bold', color: '#4CAF50', marginBottom: '2px' }}>
                        {testName.replace('_', ' ').toUpperCase()}
                    </div>
                    <div style={{ fontSize: '11px' }}>
                        <span style={{ color: '#ccc' }}>Variante:</span> <span style={{ color: '#FFD700', fontWeight: 'bold' }}>{testData.variant}</span>
                    </div>
                    {/* Mostrar weights si están disponibles */}
                    {testData.weights && (
                        <div style={{ fontSize: '9px', color: '#aaa', marginTop: '2px' }}>
                            Weights: {testData.weights.map(w => `${w.name}:${w.weight}%`).join(', ')}
                        </div>
                    )}
                    {testName === 'form_optimization' && (
                        <div style={{ fontSize: '10px', color: '#aaa', marginTop: '2px' }}>
                            {testData.variant === 'control' && '6 pasos originales'}
                            {testData.variant === 'variant_a' && '3 pasos compactos'}
                            {testData.variant === 'variant_b' && '4 pasos optimizados'}
                        </div>
                    )}
                    {testName === 'hero_message' && (
                        <div style={{ fontSize: '10px', color: '#aaa', marginTop: '2px' }}>
                            {testData.variant === 'control' && 'Mensaje técnico'}
                            {testData.variant === 'variant_a' && 'Mensaje emocional 🏠'}
                            {testData.variant === 'variant_b' && 'Mensaje urgencia 🔥'}
                        </div>
                    )}
                    {/* Botón de debug individual */}
                    <button
                        onClick={() => handleDebugReassign(testName)}
                        style={{
                            backgroundColor: '#ff9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            padding: '2px 6px',
                            fontSize: '8px',
                            cursor: 'pointer',
                            marginTop: '4px'
                        }}
                    >
                        Debug {testName}
                    </button>
                </div>
            ))}

            {/* Botones de Reset */}
            <div style={{ marginTop: '12px', borderTop: '1px solid #444', paddingTop: '8px' }}>
                <div style={{ color: '#FFD700', fontWeight: 'bold', marginBottom: '6px', fontSize: '10px' }}>
                    🔄 RESET TOOLS:
                </div>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    <button
                        onClick={handleResetAll}
                        style={{
                            backgroundColor: '#ff4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '9px',
                            cursor: 'pointer',
                            flex: 1
                        }}
                    >
                        Reset Todo
                    </button>
                    <button
                        onClick={handleClearAndReassign}
                        style={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '9px',
                            cursor: 'pointer',
                            flex: 1
                        }}
                    >
                        Reasignar
                    </button>
                    <button
                        onClick={handleShowWeights}
                        style={{
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '9px',
                            cursor: 'pointer',
                            flex: 1
                        }}
                    >
                        Show Weights
                    </button>
                </div>
            </div>

            <div style={{ marginTop: '10px', fontSize: '10px', opacity: '0.7', borderTop: '1px solid #444', paddingTop: '8px' }}>
                <div style={{ color: '#FFD700', fontWeight: 'bold', marginBottom: '4px' }}>Parámetros URL:</div>
                <div>?form=control|variant_a|variant_b</div>
                <div>?hero_message=control|variant_a|variant_b</div>
                <div style={{ color: '#4CAF50', marginTop: '4px' }}>?debug=1 (para mostrar este panel)</div>
                <div style={{ color: '#ff6666', marginTop: '4px' }}>?reset=1 (resetear todo)</div>
                <div style={{ color: '#2196F3', marginTop: '4px' }}>F12 → console → window.abTestingDebug</div>
            </div>
        </div>
    );
};

export default ABTestDebugger;
